import clsx from 'clsx';
import { ChevronDown, CircleUserRound, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useMapContext } from '@/context';
import { useERSContext } from '@/context';
import { constants, formatDistance, getResponderBadge } from '@/lib';
import { Badge } from '..';
import { useTranslation } from 'react-i18next';

export const MultiSelectDropdown = ({
  options = [],
  value = [],
  placeholder = constants.MULTI_SELECT.PLACEHOLDER,
  onChange,
  handleResponderHover,
  className = '',
  expandDirection = 'down',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const selectedIds = value;
  const dropdownRef = useRef(null);
  const { setRespondersForCreateIncident } = useMapContext();
  const { latestResponder, incidentAssigneeStatusList } = useERSContext();

  const getSelectedOption = id => {
    return options.find(opt => opt.id === id);
  };

  const { t } = useTranslation();

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  const statusIdToDisplayNameMap = (incidentAssigneeStatusList || []).reduce((acc, status) => {
    acc[status.id] = status.display_name;
    return acc;
  }, {});

  const getPresenceClass = presence => {
    const statusColors = constants.RESPONDERS.LIST.STATUS_COLORS;
    const presenceStatus = constants.RESPONDERS.LIST.PRESENCE_STATUS;

    switch (presence) {
      case presenceStatus.DISCONNECTED:
        return statusColors.DISCONNECTED;
      case presenceStatus.OFFLINE:
        return statusColors.OFFLINE;
      case presenceStatus.ONLINE:
        return statusColors.ONLINE;
      default:
        return statusColors.DISCONNECTED;
    }
  };

  const toggleSelection = id => {
    const newSelection = selectedIds.includes(id)
      ? selectedIds.filter(selectedId => selectedId !== id)
      : [...selectedIds, id];
    onChange?.(newSelection);
    setRespondersForCreateIncident(prevState =>
      prevState.map(responder =>
        responder.id === id ? { ...responder, isSelected: !responder.isSelected, isHover: false } : responder
      )
    );
  };

  const removeFromSelection = (id, e) => {
    e.stopPropagation();
    const newSelection = selectedIds.filter(selectedId => selectedId !== id);
    onChange?.(newSelection);
    setRespondersForCreateIncident(prev => prev.filter(responder => responder.id !== id));
  };

  useEffect(() => {
    const handleClickOutside = event => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      // Using 'click' instead of 'mousedown' for better touch support
      document.addEventListener('click', handleClickOutside);
    }

    if (!isOpen) setRespondersForCreateIncident([]);

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [isOpen]);

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {/* Selected Values Display */}
      <div
        tabIndex={0}
        onClick={toggleDropdown}
        className="p-1 pl-3 bg border rounded-md cursor-pointer flex items-center justify-between hover:bg hover:border-2 hover:border-focusRing focus:outline-none focus:border-focusRing focus:border-2 transition-colors"
      >
        <div className="flex gap-2 flex-wrap flex-1">
          {selectedIds.length === 0 ? (
            <span className="text-placeholder py-1">{placeholder}</span>
          ) : (
            selectedIds.map(id => {
              const option = getSelectedOption(id);
              if (!option) return null;
              return (
                <div
                  key={id}
                  className="inline-flex items-center gap-1 bg-secondary py-1 pl-2 pr-1 rounded-md text-xs border"
                >
                  <div key={id}>
                    <span>{option.displayName}</span>
                  </div>
                  <button onClick={e => removeFromSelection(id, e)} className="text-icon-muted">
                    <X className="w-3 h-3" absoluteStrokeWidth />
                  </button>
                </div>
              );
            })
          )}
        </div>
        <ChevronDown className={`text-icon-muted transition-transform flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`} />
      </div>

      {/* Dropdown List */}
      {isOpen && (
        <div
          className={`
            absolute z-10 w-full border rounded-lg bg shadow-lg max-h-44 overflow-y-auto
            ${expandDirection === 'up' ? 'bottom-full mb-1' : ''}
          `}
        >
          {/* If no options left to display */}
          {options.filter(option => !selectedIds.includes(option.id)).length === 0 ? (
            <div className="px-4 py-8 text-center text-sm">{constants.MULTI_SELECT.NO_RESPONDER_AVAILABLE}</div>
          ) : (
            options
              // Only show unselected options in the dropdown
              .filter(option => !selectedIds.includes(option.id))
              .map(option => {
                const detailRepsonderData = latestResponder?.find(r => r.id.split('@')[0] === option.id.split('@')[0]);
                return (
                  <div
                    key={option.id}
                    onClick={() => toggleSelection(option.id)}
                    onMouseEnter={() => handleResponderHover(option, true)}
                    onMouseLeave={() => handleResponderHover(option, false)}
                    className="px-4 py-3 flex items-center justify-between cursor-pointer hover:bg-subtle transition-colors border-b last:border-b-0"
                  >
                    <div className="flex items-center gap-2">
                      {/* Avatar with status indicator */}
                      {option.icon ? (
                        <div className="relative">
                          <div className="w-6 h-6 rounded-full flex items-center justify-center">{option.icon}</div>
                          {option.presence && (
                            <span
                              className={clsx(
                                'absolute bottom-0 right-0 block h-2 w-2 rounded-full border-2 border-onColor',
                                getPresenceClass(option.presence)
                              )}
                            />
                          )}
                        </div>
                      ) : (
                        <div className="relative">
                          <CircleUserRound size={24} absoluteStrokeWidth />
                          <span
                            className={clsx(
                              'absolute bottom-0 right-0 block h-2 w-2 rounded-full border-2 border-onColor',
                              getPresenceClass(option.presence)
                            )}
                          />
                        </div>
                      )}

                      <div className="flex flex-col">
                        <span className="font-medium">{option.displayName}</span>

                        {/* Assignment List - Following your working logic */}
                        <div className="flex flex-col gap-2 mt-1 border-l-2 pl-2 border-border/50">
                          {detailRepsonderData.allAssignments?.length > 0 ? (
                            detailRepsonderData.allAssignments.map((assignment, idx) => {
                              const badge = getResponderBadge(
                                {
                                  assignee: { ...assignment.assigneeRecord },
                                  statuses: assignment.statusHistory.map(d => d.status),
                                },
                                statusIdToDisplayNameMap
                              );
                              if (assignment.incidentStatus === 'resolved') return null;
                              return (
                                <div key={assignment.incidentId || idx} className="flex space-x-1">
                                  <div className={clsx('font-semibold')}>{assignment.incidentName}</div>
                                  <div className="flex items-center gap-2">
                                    <Badge
                                      variant={badge.variant}
                                      size="sm"
                                      className="text-[9px] py-0 px-1 leading-tight"
                                    >
                                      {t('RESPONDER_STATUS', { returnObjects: true }).find(
                                        status => status.value === badge.label
                                      )?.label || badge.label}
                                    </Badge>
                                    <span className="text-[9px] text-muted-foreground italic uppercase">
                                      {assignment.decision}
                                    </span>
                                  </div>
                                </div>
                              );
                            })
                          ) : (
                            <span className="text-[10px] text-muted-foreground italic">No Active Incidents</span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-4">
                      {/* Subtitle */}
                      <span className="text-muted text-sm">{formatDistance(option?.distance)}</span>

                      {/* Status */}
                      {/* {option.presence && (
                        <Badge variant={'SoftGreen'} className="text-xs">
                          {option.presence}
                        </Badge>
                      )} */}
                    </div>
                  </div>
                );
              })
          )}
        </div>
      )}
    </div>
  );
};
